import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { XIcon, BellIcon } from './Icons'
import { Property } from './PropertyManagement'
import { buildApiUrl } from '../utils/api'

interface PropertyFormProps {
  property?: Property | null
  customerId: number
  onClose: () => void
  onSave: (property: Property) => void
}

interface PropertyFormData {
  customer_id: number
  property_name: string
  address: string
  property_type: string
  number_of_units: number
  property_value: string
  purchase_price: string
  purchase_date: string
  financed_by_us: boolean
  mortgage_amount: string
  original_mortgage_amount: string
  current_interest_rate: string
  amortization_years: string
  term_years: string
  mortgage_start_date: string
  renewal_date: string
  lender_name: string
  mortgage_type: string
  payment_frequency: string
  monthly_payment: string
  bedrooms: string
  bathrooms: string
  square_feet: string
  year_built: string
  lot_size: string
  lot_size_unit: string
  is_rental_property: boolean
  monthly_rental_income: string
  annual_property_taxes: string
  annual_insurance: string
  monthly_maintenance_estimate: string
  notes: string
  status: string
  auto_renewal_reminders: boolean
  renewal_reminder_months: number
  auto_rate_review_reminders: boolean
  rate_review_frequency_months: number
}

export function PropertyForm({ property, customerId, onClose, onSave }: PropertyFormProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState<'basic' | 'mortgage' | 'details' | 'rental' | 'automation'>('basic')

  const [formData, setFormData] = useState<PropertyFormData>({
    customer_id: customerId,
    property_name: '',
    address: '',
    property_type: 'residential',
    number_of_units: 1,
    property_value: '',
    purchase_price: '',
    purchase_date: '',
    financed_by_us: false,
    mortgage_amount: '',
    original_mortgage_amount: '',
    current_interest_rate: '',
    amortization_years: '',
    term_years: '',
    mortgage_start_date: '',
    renewal_date: '',
    lender_name: '',
    mortgage_type: 'fixed',
    payment_frequency: 'monthly',
    monthly_payment: '',
    bedrooms: '',
    bathrooms: '',
    square_feet: '',
    year_built: '',
    lot_size: '',
    lot_size_unit: 'sqft',
    is_rental_property: false,
    monthly_rental_income: '',
    annual_property_taxes: '',
    annual_insurance: '',
    monthly_maintenance_estimate: '',
    notes: '',
    status: 'active',
    auto_renewal_reminders: true,
    renewal_reminder_months: 6,
    auto_rate_review_reminders: false,
    rate_review_frequency_months: 12
  })

  useEffect(() => {
    if (property) {
      setFormData({
        customer_id: property.customer_id,
        property_name: property.property_name || '',
        address: property.address || '',
        property_type: property.property_type || 'residential',
        number_of_units: property.number_of_units || 1,
        property_value: property.property_value?.toString() || '',
        purchase_price: property.purchase_price?.toString() || '',
        purchase_date: property.purchase_date || '',
        financed_by_us: property.financed_by_us || false,
        mortgage_amount: property.mortgage_amount?.toString() || '',
        original_mortgage_amount: property.original_mortgage_amount?.toString() || '',
        current_interest_rate: property.current_interest_rate?.toString() || '',
        amortization_years: property.amortization_years?.toString() || '',
        term_years: property.term_years?.toString() || '',
        mortgage_start_date: property.mortgage_start_date || '',
        renewal_date: property.renewal_date || '',
        lender_name: property.lender_name || '',
        mortgage_type: property.mortgage_type || 'fixed',
        payment_frequency: property.payment_frequency || 'monthly',
        monthly_payment: property.monthly_payment?.toString() || '',
        bedrooms: property.bedrooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        square_feet: property.square_feet?.toString() || '',
        year_built: property.year_built?.toString() || '',
        lot_size: property.lot_size?.toString() || '',
        lot_size_unit: property.lot_size_unit || 'sqft',
        is_rental_property: property.is_rental_property || false,
        monthly_rental_income: property.monthly_rental_income?.toString() || '',
        annual_property_taxes: property.annual_property_taxes?.toString() || '',
        annual_insurance: property.annual_insurance?.toString() || '',
        monthly_maintenance_estimate: property.monthly_maintenance_estimate?.toString() || '',
        notes: property.notes || '',
        status: property.status || 'active',
        auto_renewal_reminders: property.auto_renewal_reminders ?? true,
        renewal_reminder_months: property.renewal_reminder_months || 6,
        auto_rate_review_reminders: property.auto_rate_review_reminders || false,
        rate_review_frequency_months: property.rate_review_frequency_months || 12
      })
    }
  }, [property, customerId])

  const handleInputChange = (field: keyof PropertyFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('Authentication required')

      // Convert string values to numbers where needed
      const processedData = {
        ...formData,
        property_value: formData.property_value ? parseFloat(formData.property_value) : undefined,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : undefined,
        mortgage_amount: formData.mortgage_amount ? parseFloat(formData.mortgage_amount) : undefined,
        original_mortgage_amount: formData.original_mortgage_amount ? parseFloat(formData.original_mortgage_amount) : undefined,
        current_interest_rate: formData.current_interest_rate ? parseFloat(formData.current_interest_rate) : undefined,
        amortization_years: formData.amortization_years ? parseInt(formData.amortization_years) : undefined,
        term_years: formData.term_years ? parseInt(formData.term_years) : undefined,
        monthly_payment: formData.monthly_payment ? parseFloat(formData.monthly_payment) : undefined,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : undefined,
        square_feet: formData.square_feet ? parseInt(formData.square_feet) : undefined,
        year_built: formData.year_built ? parseInt(formData.year_built) : undefined,
        lot_size: formData.lot_size ? parseFloat(formData.lot_size) : undefined,
        monthly_rental_income: formData.monthly_rental_income ? parseFloat(formData.monthly_rental_income) : undefined,
        annual_property_taxes: formData.annual_property_taxes ? parseFloat(formData.annual_property_taxes) : undefined,
        annual_insurance: formData.annual_insurance ? parseFloat(formData.annual_insurance) : undefined,
        monthly_maintenance_estimate: formData.monthly_maintenance_estimate ? parseFloat(formData.monthly_maintenance_estimate) : undefined,
      }

      const url = property ? buildApiUrl(`api/properties/${property.id}`) : buildApiUrl('api/properties')
      const method = property ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(processedData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to save property')
      }

      const result = await response.json()
      onSave(result.property)
    } catch (err) {
      console.error('Error saving property:', err)
      setError(err instanceof Error ? err.message : 'Failed to save property')
    } finally {
      setLoading(false)
    }
  }

  const sections = [
    { id: 'basic', label: t('customers.propertyForm.tabs.basicInfo'), icon: '🏠' },
    { id: 'mortgage', label: t('customers.propertyForm.tabs.mortgageDetails'), icon: '💰' },
    { id: 'details', label: t('customers.propertyForm.tabs.propertyDetails'), icon: '📐' },
    { id: 'rental', label: t('customers.propertyForm.tabs.rentalInfo'), icon: '🏘️' },
    { id: 'automation', label: t('customers.propertyForm.tabs.automation'), icon: '🤖' }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-6xl h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {property ? t('customers.propertyForm.title.edit') : t('customers.propertyForm.title.addNew')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Section Navigation */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto flex-shrink-0">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id as any)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeSection === section.id
                  ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span>{section.icon}</span>
              <span>{section.label}</span>
            </button>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <div className="flex">
                  <BellIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{t('customers.propertyForm.error')}</h3>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Information Section */}
            {activeSection === 'basic' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('customers.propertyForm.sections.basicInfo.title')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.propertyName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.property_name}
                      onChange={(e) => handleInputChange('property_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.basicInfo.placeholders.propertyName')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.propertyType')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.property_type}
                      onChange={(e) => handleInputChange('property_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      required
                    >
                      <option value="residential">{t('customers.propertyForm.sections.basicInfo.options.propertyTypes.residential')}</option>
                      <option value="commercial">{t('customers.propertyForm.sections.basicInfo.options.propertyTypes.commercial')}</option>
                      <option value="multi_family">{t('customers.propertyForm.sections.basicInfo.options.propertyTypes.multiFamily')}</option>
                      <option value="condo">{t('customers.propertyForm.sections.basicInfo.options.propertyTypes.condo')}</option>
                      <option value="townhouse">{t('customers.propertyForm.sections.basicInfo.options.propertyTypes.townhouse')}</option>
                      <option value="land">{t('customers.propertyForm.sections.basicInfo.options.propertyTypes.land')}</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.address')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      rows={2}
                      placeholder={t('customers.propertyForm.sections.basicInfo.placeholders.address')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.numberOfUnits')}
                    </label>
                    <input
                      type="number"
                      value={formData.number_of_units}
                      onChange={(e) => handleInputChange('number_of_units', parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.status')}
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      <option value="active">{t('customers.propertyForm.sections.basicInfo.options.status.active')}</option>
                      <option value="sold">{t('customers.propertyForm.sections.basicInfo.options.status.sold')}</option>
                      <option value="under_renovation">{t('customers.propertyForm.sections.basicInfo.options.status.underRenovation')}</option>
                      <option value="for_sale">{t('customers.propertyForm.sections.basicInfo.options.status.forSale')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.propertyValue')}
                    </label>
                    <input
                      type="number"
                      value={formData.property_value}
                      onChange={(e) => handleInputChange('property_value', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.basicInfo.placeholders.propertyValue')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.purchasePrice')}
                    </label>
                    <input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => handleInputChange('purchase_price', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.basicInfo.placeholders.purchasePrice')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.basicInfo.fields.purchaseDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => handleInputChange('purchase_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Mortgage Details Section */}
            {activeSection === 'mortgage' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('customers.propertyForm.sections.mortgageInfo.title')}</h3>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.financed_by_us}
                      onChange={(e) => handleInputChange('financed_by_us', e.target.checked)}
                      className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('customers.propertyForm.sections.mortgageInfo.financedByUs')}</span>
                  </label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.mortgageAmount')}
                    </label>
                    <input
                      type="number"
                      value={formData.mortgage_amount}
                      onChange={(e) => handleInputChange('mortgage_amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.mortgageAmount')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.originalMortgageAmount')}
                    </label>
                    <input
                      type="number"
                      value={formData.original_mortgage_amount}
                      onChange={(e) => handleInputChange('original_mortgage_amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.originalMortgageAmount')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.currentInterestRate')} (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.current_interest_rate}
                      onChange={(e) => handleInputChange('current_interest_rate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.currentInterestRate')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.lenderName')}
                    </label>
                    <input
                      type="text"
                      value={formData.lender_name}
                      onChange={(e) => handleInputChange('lender_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.lenderName')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.amortizationYears')}
                    </label>
                    <input
                      type="number"
                      value={formData.amortization_years}
                      onChange={(e) => handleInputChange('amortization_years', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.amortizationYears')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.termYears')}
                    </label>
                    <input
                      type="number"
                      value={formData.term_years}
                      onChange={(e) => handleInputChange('term_years', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.termYears')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.mortgageType')}
                    </label>
                    <select
                      value={formData.mortgage_type}
                      onChange={(e) => handleInputChange('mortgage_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      <option value="fixed">{t('customers.propertyForm.sections.mortgageInfo.options.mortgageTypes.fixed')}</option>
                      <option value="variable">{t('customers.propertyForm.sections.mortgageInfo.options.mortgageTypes.variable')}</option>
                      <option value="hybrid">{t('customers.propertyForm.sections.mortgageInfo.options.mortgageTypes.hybrid')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.paymentFrequency')}
                    </label>
                    <select
                      value={formData.payment_frequency}
                      onChange={(e) => handleInputChange('payment_frequency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      <option value="monthly">{t('customers.propertyForm.sections.mortgageInfo.options.paymentFrequencies.monthly')}</option>
                      <option value="bi_weekly">{t('customers.propertyForm.sections.mortgageInfo.options.paymentFrequencies.biWeekly')}</option>
                      <option value="weekly">{t('customers.propertyForm.sections.mortgageInfo.options.paymentFrequencies.weekly')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.mortgageStartDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.mortgage_start_date}
                      onChange={(e) => handleInputChange('mortgage_start_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.renewalDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.renewal_date}
                      onChange={(e) => handleInputChange('renewal_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.mortgageInfo.fields.monthlyPayment')}
                    </label>
                    <input
                      type="number"
                      value={formData.monthly_payment}
                      onChange={(e) => handleInputChange('monthly_payment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.mortgageInfo.fields.monthlyPayment')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Property Details Section */}
            {activeSection === 'details' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('customers.propertyForm.sections.propertyDetails.title')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.bedrooms')}
                    </label>
                    <input
                      type="number"
                      value={formData.bedrooms}
                      onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.bathrooms')}
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={formData.bathrooms}
                      onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.squareFeet')}
                    </label>
                    <input
                      type="number"
                      value={formData.square_feet}
                      onChange={(e) => handleInputChange('square_feet', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.propertyDetails.fields.squareFeet')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.yearBuilt')}
                    </label>
                    <input
                      type="number"
                      value={formData.year_built}
                      onChange={(e) => handleInputChange('year_built', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      min="1800"
                      max={new Date().getFullYear()}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.lotSize')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.lot_size}
                      onChange={(e) => handleInputChange('lot_size', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.propertyDetails.fields.lotSize')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.lotSizeUnit')}
                    </label>
                    <select
                      value={formData.lot_size_unit}
                      onChange={(e) => handleInputChange('lot_size_unit', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      <option value="sqft">{t('customers.propertyForm.sections.propertyDetails.options.lotSizeUnits.sqft')}</option>
                      <option value="acres">{t('customers.propertyForm.sections.propertyDetails.options.lotSizeUnits.acres')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.annualPropertyTaxes')}
                    </label>
                    <input
                      type="number"
                      value={formData.annual_property_taxes}
                      onChange={(e) => handleInputChange('annual_property_taxes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.propertyDetails.fields.annualPropertyTaxes')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.annualInsurance')}
                    </label>
                    <input
                      type="number"
                      value={formData.annual_insurance}
                      onChange={(e) => handleInputChange('annual_insurance', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.propertyDetails.fields.annualInsurance')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.propertyForm.sections.propertyDetails.fields.monthlyMaintenanceEstimate')}
                    </label>
                    <input
                      type="number"
                      value={formData.monthly_maintenance_estimate}
                      onChange={(e) => handleInputChange('monthly_maintenance_estimate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      placeholder={t('customers.propertyForm.sections.propertyDetails.fields.monthlyMaintenanceEstimate')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.propertyForm.sections.propertyDetails.fields.notes')}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    rows={3}
                    placeholder={t('customers.propertyForm.sections.propertyDetails.fields.notes')}
                  />
                </div>
              </div>
            )}

            {/* Rental Information Section */}
            {activeSection === 'rental' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('customers.propertyForm.sections.rentalInfo.title')}</h3>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_rental_property}
                      onChange={(e) => handleInputChange('is_rental_property', e.target.checked)}
                      className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('customers.propertyForm.sections.rentalInfo.isRentalProperty')}</span>
                  </label>
                </div>

                {formData.is_rental_property && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('customers.propertyForm.sections.rentalInfo.fields.monthlyRentalIncome')}
                      </label>
                      <input
                        type="number"
                        value={formData.monthly_rental_income}
                        onChange={(e) => handleInputChange('monthly_rental_income', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                        placeholder={t('customers.propertyForm.sections.rentalInfo.fields.monthlyRentalIncome')}
                      />
                    </div>
                  </div>
                )}

                {!formData.is_rental_property && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>{t('customers.propertyForm.sections.rentalInfo.isRentalProperty')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Automation Section */}
            {activeSection === 'automation' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('customers.propertyForm.sections.automation.title')}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.auto_renewal_reminders}
                      onChange={(e) => handleInputChange('auto_renewal_reminders', e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('customers.propertyForm.sections.automation.autoRenewalReminders')}
                      </label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('customers.propertyForm.sections.automation.autoRenewalReminders')}
                      </p>
                      {formData.auto_renewal_reminders && (
                        <div className="mt-2">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {t('customers.propertyForm.sections.automation.renewalReminderMonths')}
                          </label>
                          <input
                            type="number"
                            value={formData.renewal_reminder_months}
                            onChange={(e) => handleInputChange('renewal_reminder_months', parseInt(e.target.value) || 6)}
                            className="w-24 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                            min="1"
                            max="12"
                          />
                          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{t('customers.propertyForm.sections.automation.renewalReminderMonths')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.auto_rate_review_reminders}
                      onChange={(e) => handleInputChange('auto_rate_review_reminders', e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('customers.propertyForm.sections.automation.autoRateReviewReminders')}
                      </label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('customers.propertyForm.sections.automation.autoRateReviewReminders')}
                      </p>
                      {formData.auto_rate_review_reminders && (
                        <div className="mt-2">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {t('customers.propertyForm.sections.automation.rateReviewFrequencyMonths')}
                          </label>
                          <select
                            value={formData.rate_review_frequency_months}
                            onChange={(e) => handleInputChange('rate_review_frequency_months', parseInt(e.target.value) || 12)}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                          >
                            <option value="6">{t('customers.propertyForm.sections.automation.options.renewalReminderMonths.6')}</option>
                            <option value="12">{t('customers.propertyForm.sections.automation.options.renewalReminderMonths.12')}</option>
                            <option value="24">{t('customers.propertyForm.sections.automation.options.renewalReminderMonths.24')}</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
              disabled={loading}
            >
              {t('customers.propertyForm.buttons.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? t('customers.propertyForm.buttons.saving') : (property ? t('customers.propertyForm.buttons.updateProperty') : t('customers.propertyForm.buttons.createProperty'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
